import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Form, Input, Modal, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { DateRangePicker as ZhDateRangePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { DEFAULT_VALUE, LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { CUSTOMER_ADVANCE_PAY_STATE } from "@/meta/expenseManage/customerAdvancePayment";
import { dateFormat, dateFullTimeFormat } from "@/utils";
import { formatNumber } from "@/utils/number";
import Permission from "@/views/components/Permission";
import { useUserStore } from "@/views/stores";

export interface WillPayQuery {
    page: number;
    size: number;
    id?: string;
    customerInfo?: string;
    startTime?: string;
    endTime?: string;
    state?: number;
    sortKey?: string;
    sortOrder?: "asc" | "desc";
}

export interface WillPayRow {
    id: number | string;
    customerInfo?: { name?: string; realName?: string; mobile?: string; code?: string };
    amount?: number;
    date?: string;
    state?: number | string;
}

export interface WillPayResult {
    list?: WillPayRow[];
    total?: number;
}

/** 复用 Vue 用户账户预付款分页接口。 */
export async function loadWillPayRecords(query: WillPayQuery): Promise<WillPayResult> {
    const { data } = await request.service.get<WillPayResult>("/customer/advance-payment/paging", { params: query });
    return data;
}

/** 复用 Vue 待付款列表撤销接口，保持列表操作的 payload 契约。 */
export async function revokeWillPayRecord(params: { id: number | string; remark: string }) {
    const { data } = await request.service.put("/customer/advance-payment/revoke", params);
    return data;
}

const customerText = (customer?: WillPayRow["customerInfo"]) =>
    customer
        ? [customer.name, customer.realName, customer.mobile, customer.code].filter(Boolean).join(" | ")
        : DEFAULT_VALUE;

/** 费用管理预付款列表，补齐 Vue 新增、详情和撤销操作闭环。 */
export function WillPayPage({
    loadRecords = loadWillPayRecords,
    revokeRecord = revokeWillPayRecord
}: {
    loadRecords?: (query: WillPayQuery) => Promise<WillPayResult>;
    revokeRecord?: (params: { id: number | string; remark: string }) => Promise<unknown>;
}) {
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const [records, setRecords] = useState<WillPayRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<WillPayQuery>({ page: 1, size: 10 });
    const [id, setId] = useState("");
    const [customerInfo, setCustomerInfo] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [state, setState] = useState("");
    const [error, setError] = useState("");
    const [revokeId, setRevokeId] = useState<number | string>();
    const [revokeReason, setRevokeReason] = useState("");
    const [revokeError, setRevokeError] = useState("");
    const [revokeLoading, setRevokeLoading] = useState(false);

    useEffect(() => {
        let disposed = false;
        void loadRecords(query)
            .then(result => {
                if (disposed) return;
                setRecords(result.list || []);
                setTotal(result.total || 0);
                setError("");
            })
            .catch(() => {
                if (disposed) return;
                setRecords([]);
                setTotal(0);
                setError("预付款列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadRecords, query]);

    // 将 Vue ProTable 的筛选草稿转换为分页接口参数，日期统一使用旧接口的边界时间格式。
    const submitFilters = () => {
        if (startDate && endDate && startDate > endDate) {
            setError("付款日期范围无效");
            return;
        }
        const nextQuery: WillPayQuery = { page: 1, size: 10 };
        if (id.trim()) nextQuery.id = id.trim();
        if (customerInfo.trim()) nextQuery.customerInfo = customerInfo.trim();
        if (startDate) nextQuery.startTime = dateFormat(startDate, LOG_DATE_FORMAT_START);
        if (endDate) nextQuery.endTime = dateFullTimeFormat(endDate, LOG_DATE_FORMAT_END);
        if (state) nextQuery.state = Number(state);
        setError("");
        setQuery(nextQuery);
    };

    // 重置筛选并回到 Vue ProTable 的默认首屏参数。
    const resetFilters = () => {
        setId("");
        setCustomerInfo("");
        setStartDate("");
        setEndDate("");
        setState("");
        setError("");
        setQuery({ page: 1, size: 10 });
    };

    // 按 Vue 列表行为打开撤销弹窗，并清理上一次输入和错误状态。
    const openRevoke = (recordId: number | string) => {
        setRevokeId(recordId);
        setRevokeReason("");
        setRevokeError("");
    };

    // 撤销成功后刷新当前查询，失败时保留弹窗和用户输入，便于继续修正或重试。
    const submitRevoke = async () => {
        if (revokeId == null) return;
        const remark = revokeReason.trim();
        if (!remark) {
            setRevokeError("请输入撤销原因");
            return;
        }
        setRevokeLoading(true);
        setRevokeError("");
        try {
            await revokeRecord({ id: revokeId, remark });
            notifyOperationSuccess("已撤销");
            setRevokeId(undefined);
            setRevokeReason("");
            setQuery(current => ({ ...current }));
        } catch {
            setRevokeError("预付款撤销失败");
        } finally {
            setRevokeLoading(false);
        }
    };

    // 用 zhihao-ui-react 列契约承接 Vue 待付款列表的字段、详情和撤销操作。
    const columns: TableColumnProps<WillPayRow>[] = [
        {
            key: "id",
            label: "编号",
            minWidth: 180,
            prop: "id",
            render: ({ row }) => <Link to={`/expenseManagement/userAccount/willPay/detail/${row.id}`}>{row.id}</Link>
        },
        {
            key: "customerInfo",
            label: "用户账户",
            minWidth: 400,
            prop: "customerInfo",
            render: ({ row }) => customerText(row.customerInfo)
        },
        {
            key: "amount",
            label: "金额(元)",
            minWidth: 228,
            prop: "amount",
            render: ({ row }) => (row.amount == null ? DEFAULT_VALUE : formatNumber(row.amount, 2))
        },
        {
            key: "date",
            label: "付款日期",
            minWidth: 214,
            prop: "date",
            sortable: true,
            render: ({ row }) => (row.date ? dateFormat(row.date) : DEFAULT_VALUE)
        },
        {
            key: "state",
            label: "状态",
            minWidth: 214,
            prop: "state",
            render: ({ row }) => CUSTOMER_ADVANCE_PAY_STATE.getIntroByCode(Number(row.state)) || DEFAULT_VALUE
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            width: 200,
            prop: "id",
            render: ({ row }) =>
                row.state === CUSTOMER_ADVANCE_PAY_STATE.SUCCESS.code ? (
                    <Permission anyOf={["ROLE_ACCOUNT_MANAGE"]}>
                        <Button aria-label="撤销" disabled={banButton} onClick={() => openRevoke(row.id)} type="link">
                            撤销
                        </Button>
                    </Permission>
                ) : null
        }
    ];

    return (
        <section aria-label="will-pay-page">
            <Card>
                <ZhTable<WillPayRow>
                    data={records}
                    columns={normalizeOperationColumns(columns, records)}
                    pagination={false}
                    rowKey="id"
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title level={4} className="mb-4 mt-0">
                                待付款
                            </Typography.Title>

                            {error ? <Alert role="alert" title={error} type="error" /> : null}

                            <div className="mb-4 flex">
                                <Permission anyOf={["ROLE_ACCOUNT_MANAGE"]}>
                                    <Button
                                        autoInsertSpace={false}
                                        disabled={banButton}
                                        onClick={() => void navigate("/expenseManagement/userAccount/willPay/add")}
                                        type="primary"
                                    >
                                        添加预付款
                                    </Button>
                                </Permission>
                            </div>

                            <Form className="admin-table-filter-form mb-4" layout="inline" onFinish={submitFilters}>
                                <Form.Item>
                                    <Input
                                        aria-label="预付款编号"
                                        placeholder="请输入编号搜索"
                                        value={id}
                                        onChange={event => setId(event.target.value)}
                                    />
                                </Form.Item>
                                <Form.Item>
                                    <Input
                                        aria-label="用户账户"
                                        placeholder="请输入账号信息搜索"
                                        value={customerInfo}
                                        onChange={event => setCustomerInfo(event.target.value)}
                                    />
                                </Form.Item>
                                <Form.Item>
                                    <ZhDateRangePicker
                                        clearable
                                        endPlaceholder="结束时间"
                                        format="YYYY-MM-DD"
                                        rangeSeparator="-"
                                        startPlaceholder="起始时间"
                                        type="daterange"
                                        value={startDate || endDate ? [startDate, endDate] : null}
                                        valueFormat="YYYY-MM-DD"
                                        onChange={(_value, formattedValue) => {
                                            setStartDate(formattedValue?.[0] || "");
                                            setEndDate(formattedValue?.[1] || "");
                                        }}
                                    />
                                </Form.Item>
                                <Form.Item>
                                    <Select
                                        allowClear
                                        aria-label="预付款状态"
                                        options={CUSTOMER_ADVANCE_PAY_STATE.options.map(option => ({
                                            label: option.label,
                                            value: String(option.value)
                                        }))}
                                        placeholder="请选择状态"
                                        value={state || undefined}
                                        onChange={value => setState(value || "")}
                                    />
                                </Form.Item>
                                <Space>
                                    <Button aria-label="搜索" autoInsertSpace={false} htmlType="submit" type="primary">
                                        搜索
                                    </Button>
                                    <Button autoInsertSpace={false} onClick={resetFilters}>
                                        重置
                                    </Button>
                                </Space>
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                total={total}
                                hideOnSinglePage
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <Modal
                title="撤销"
                open={revokeId != null}
                okText="确定"
                cancelText="取消"
                confirmLoading={revokeLoading}
                onCancel={() => setRevokeId(undefined)}
                onOk={() => void submitRevoke()}
            >
                <Typography.Text>撤销后账户余额将发生变化，请谨慎操作</Typography.Text>
                <Input.TextArea
                    aria-label="撤销原因"
                    className="mt-4"
                    maxLength={100}
                    placeholder="请输入撤销原因"
                    rows={3}
                    value={revokeReason}
                    onChange={event => setRevokeReason(event.target.value)}
                />
                {revokeError ? <Alert className="mt-2" message={revokeError} role="alert" type="error" /> : null}
            </Modal>
        </section>
    );
}

export default WillPayPage;
